import { NextRequest } from 'next/server';
import pino from 'pino';

import {
  LogCategory,
  LogEvent,
  LogLevel,
  LogLevelDesc,
  LogMetadata,
  MAX_LOG_BODY_LENGTH,
} from './constants';
import type { CNTXTLogger, LogData, Session } from './types';

/**
  TRACE: 0;
  DEBUG: 1;
  INFO: 2;
  WARN: 3;
  ERROR: 4;
  SILENT: 5;
 */
// Get log level from environment variable, fallback to WARN.
// Reads from process.env so consumers (e.g. workflow-engine-api) are not forced to load @cntxt/shared/utilities/envs.
const getDefaultLogLevel = (): LogLevelDesc => {
  const level =
    process.env.NEXT_PUBLIC_LOG_LEVEL ?? process.env.LOG_LEVEL ?? '';
  return Object.values(LogLevel).includes(level as LogLevelDesc)
    ? (level as LogLevelDesc)
    : LogLevel.WARN;
};

/**
 * Creates a base Pino logger instance with runtime-specific configuration
 * Handles Edge Runtime, Server, and Browser environments differently
 */
const createBasePinoLogger = (): pino.Logger => {
  const isEdgeRuntime = process.env.NEXT_RUNTIME === 'edge';
  const isServer = globalThis.window === undefined;
  const currentLogLevel = getDefaultLogLevel();

  const baseConfig = {
    level: currentLogLevel.toLowerCase(),
    formatters: {
      level: (label: string) => ({ severity: label.toUpperCase() }),
    },
    timestamp: pino.stdTimeFunctions.isoTime,
    base: {
      service: 'work-permit',
      version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
    },
  };

  if (isEdgeRuntime) {
    return pino({
      ...baseConfig,
      browser: {
        asObject: true,
        write: {
          info: (o: object) => console.info(JSON.stringify(o)),
          warn: (o: object) => console.warn(JSON.stringify(o)),
          error: (o: object) => console.error(JSON.stringify(o)),
          debug: (o: object) => console.debug(JSON.stringify(o)),
          trace: (o: object) => console.trace(JSON.stringify(o)),
        },
      },
    });
  }

  if (isServer) {
    // Use pretty printing in local development for better readability
    const isDevelopment = process.env.NODE_ENV === 'development';

    return pino({
      ...baseConfig,
      ...(isDevelopment && {
        transport: {
          target: 'pino-pretty',
          options: {
            colorize: true,
            translateTime: 'HH:MM:ss',
            ignore: 'pid,hostname,service,version',
            singleLine: false,
          },
        },
      }),
    });
  }

  return pino({
    ...baseConfig,
    browser: {
      asObject: true,
    },
  });
};

const basePinoLogger: pino.Logger = createBasePinoLogger();

const loggers: Record<string, CNTXTLogger> = {};

const getHostWithoutPort = (host: string): string => {
  if (!host || host === 'unknown') return 'unknown';
  const [firstHost] = host.split(',');
  const normalized = firstHost.trim();
  if (!normalized) return 'unknown';
  if (normalized.startsWith('[') && normalized.includes(']')) {
    return normalized.slice(1, normalized.indexOf(']'));
  }
  if (normalized.includes(':')) {
    const parts = normalized.split(':');
    if (parts.length > 1) {
      return parts.slice(0, -1).join(':');
    }
  }
  return normalized;
};

const getPortFromHost = (host: string): string => {
  if (!host || host === 'unknown') return 'unknown';
  const [firstHost] = host.split(',');
  const normalized = firstHost.trim();
  if (!normalized) return 'unknown';
  if (normalized.startsWith('[') && normalized.includes(']:')) {
    return normalized.slice(normalized.lastIndexOf(':') + 1);
  }
  const parts = normalized.split(':');
  if (parts.length > 1) {
    const candidate = parts[parts.length - 1] ?? '';
    return /^\d+$/.test(candidate) ? candidate : 'unknown';
  }
  return 'unknown';
};

const getSourceIpAddress = (req?: NextRequest): string => {
  if (!req) return 'unknown';
  const headers = req.headers;
  return (
    headers.get('x-forwarded-for')?.split(',')[0]?.trim() ||
    headers.get('x-real-ip') ||
    headers.get('cf-connecting-ip') ||
    'unknown'
  );
};

const getLoggerLevel = (customLevel: string | undefined): LogLevelDesc => {
  const defaultLevel = getDefaultLogLevel();
  if (!customLevel) return defaultLevel;
  return Object.values(LogLevel).includes(customLevel as LogLevelDesc)
    ? (customLevel as LogLevelDesc)
    : defaultLevel;
};

/**
 * Truncates log request body
 * @param value - The value to potentially truncate
 * @returns Truncated string if it's a long string in a truncatable field, otherwise original value
 */
const truncateRequestBody = (value: string) => {
  if (
    value &&
    typeof value === 'string' &&
    value.length > MAX_LOG_BODY_LENGTH
  ) {
    return (
      value.slice(0, MAX_LOG_BODY_LENGTH) +
      `... [truncated ${value.length - MAX_LOG_BODY_LENGTH} chars]`
    );
  }
  return value;
};

/**
 * Creates structured log data with metadata
 */
const createLogData = (
  _severity: LogLevelDesc,
  message: string,
  session: Session | undefined,
  metadata: LogMetadata | undefined,
  loggerName: string,
): LogData => {
  const safeMetadata = (metadata ?? {}) as LogMetadata;
  const {
    event,
    category,
    eventCategory,
    correlationId,
    workflowId,
    permitId,
    userId,
    tenantId,
    duration,
    error,
    rawBody,
    request: req,
    ...restMetadata
  } = safeMetadata;
  const resolvedTenantId = tenantId ?? session?.user?.tenantId;

  const logData: LogData = {
    message,
    logName: loggerName,
    timestamp: new Date().toISOString(),
    // Google Cloud Logging labels
    labels: {
      name: loggerName,
      ...(event && { event }),
      ...(category && { category }),
      ...(correlationId && { correlationId }),
      ...(workflowId && { workflowId }),
      ...(permitId && { permitId }),
      ...(userId && { userId }),
      ...(resolvedTenantId !== undefined && {
        tenantId: String(resolvedTenantId),
      }),
    },
    ...(session?.user && { user: session.user }),
    eventType: event ?? 'unknown',
    eventCategory: eventCategory ?? 'unknown',
    eventSeverity: _severity,
    loggedUserId: userId ?? 'unknown',
    tenantId: resolvedTenantId ?? 'unknown',
    ...(duration && { duration }),
    ...(rawBody && { rawBody: truncateRequestBody(rawBody) }),
    ...restMetadata,
    ...extractRequestMetadata(req),
  };

  // Add error information if present
  if (error) {
    logData.error = {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  return logData;
};

/**
 * Logs data using the appropriate Pino method based on severity
 */
const logWithPino = (
  pinoLogger: pino.Logger,
  severity: LogLevelDesc,
  logData: LogData,
): void => {
  switch (severity) {
    case LogLevel.TRACE:
      pinoLogger.trace(logData);
      break;
    case LogLevel.DEBUG:
      pinoLogger.debug(logData);
      break;
    case LogLevel.INFO:
      pinoLogger.info(logData);
      break;
    case LogLevel.WARN:
      pinoLogger.warn(logData);
      break;
    case LogLevel.ERROR:
      pinoLogger.error(logData);
      break;
    default:
      pinoLogger.info(logData);
  }
};

// Helper function to extract meaningful request metadata
const extractRequestMetadata = (req?: NextRequest) => {
  if (!req) return {};

  const headers = req.headers;
  const hostName = headers.get('host') || 'unknown';
  const destinationPort =
    headers.get('x-forwarded-port') || getPortFromHost(hostName);
  const sourceIpAddress = getSourceIpAddress(req);
  const destinationIpAddress = getHostWithoutPort(
    headers.get('x-forwarded-host') || hostName,
  );

  return {
    method: req.method,
    url: req.url,
    userAgent: headers.get('user-agent') || 'unknown',
    referer: headers.get('referer') || 'unknown',
    scopeId: sourceIpAddress,
    hostName,
    sourceIpAddress,
    destinationIpAddress,
    sourcePort:
      headers.get('x-forwarded-client-port') ||
      headers.get('x-forwarded-port') ||
      'unknown',
    destinationPort: destinationPort || 'unknown',
  };
};

/**
 * Creates a logger instance with the specified name and configuration
 * @param name - Logger name for identification
 * @param enableLogging - Whether logging is enabled (default: true)
 * @returns Configured logger instance
 */
const getLogger = (name: string, enableLogging = true): CNTXTLogger => {
  if (loggers[name]) {
    return loggers[name];
  }

  const customLevel = process.env[`LOG_LEVEL_${name.toUpperCase()}`];
  const logLevel = getLoggerLevel(customLevel);

  // Create a child logger with the specific name and level
  const pinoLogger = basePinoLogger.child(
    { name },
    { level: enableLogging ? logLevel?.toLowerCase() : 'silent' },
  );

  // Create the CNTXTLogger wrapper
  const currentLogger: CNTXTLogger = {
    contextualLog: (severity, message, session, metadata) => {
      const logData = createLogData(severity, message, session, metadata, name);
      logWithPino(pinoLogger, severity, logData);
    },
    // Legacy methods - deprecated, use contextualLog instead
    trace: (message: string, ...args: unknown[]) =>
      currentLogger.contextualLog(LogLevel.TRACE, message, undefined, {
        category: LogCategory.LEGACY,
        event: LogEvent.LEGACY_LOG,
        ...(args.length > 0 && { args }),
      }),
    debug: (message: string, ...args: unknown[]) =>
      currentLogger.contextualLog(LogLevel.DEBUG, message, undefined, {
        category: LogCategory.LEGACY,
        event: LogEvent.LEGACY_LOG,
        ...(args.length > 0 && { args }),
      }),
    info: (message: string, ...args: unknown[]) =>
      currentLogger.contextualLog(LogLevel.INFO, message, undefined, {
        category: LogCategory.LEGACY,
        event: LogEvent.LEGACY_LOG,
        ...(args.length > 0 && { args }),
      }),
    log: (message: string, ...args: unknown[]) =>
      currentLogger.contextualLog(LogLevel.INFO, message, undefined, {
        category: LogCategory.LEGACY,
        event: LogEvent.LEGACY_LOG,
        ...(args.length > 0 && { args }),
      }),
    warn: (message: string, ...args: unknown[]) =>
      currentLogger.contextualLog(LogLevel.WARN, message, undefined, {
        category: LogCategory.LEGACY,
        event: LogEvent.LEGACY_LOG,
        ...(args.length > 0 && { args }),
      }),
    error: (message: string, ...args: unknown[]) =>
      currentLogger.contextualLog(LogLevel.ERROR, message, undefined, {
        category: LogCategory.LEGACY,
        event: LogEvent.LEGACY_LOG,
        ...(args.length > 0 && { args }),
      }),
  };

  loggers[name] = currentLogger;
  return currentLogger;
};

export { LogLevel } from './constants';
export { getLogger };
